import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { School, CheckCircle2, Building2, Mail, Phone, Globe, FileText } from "lucide-react";
import { api, V1 } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { turkeyData, provinces } from "@/data/turkeyData";

export default function SchoolSignup() {
  const [form, setForm] = useState({
    school_name: "", province: "", district: "",
    contact_name: "", contact_email: "", contact_phone: "",
    website: "", short_intro: "", document_url: "",
  });
  const [sent, setSent] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => {
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (k === "province") {
        next.district = "";
      }
      return next;
    });
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.document_url) delete payload.document_url;
      if (!payload.website) delete payload.website;
      const { data } = await api.post(`${V1}/school-signups`, payload);
      setSent(data);
      toast.success("Başvurunuz alındı");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Başvuru gönderilemedi");
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center" data-testid="signup-success">
        <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-success/10 text-success mb-4">
          <CheckCircle2 className="h-8 w-8" />
        </div>
        <h1 className="font-display text-3xl font-bold">Başvurunuz Alındı</h1>
        <p className="text-muted-foreground mt-3">
          Yönetici ekibimiz başvurunuzu 1-3 iş günü içinde inceleyecek.
          Onaylandığında iletişim e-postanıza giriş bilgileri gönderilecek.
        </p>
        <div className="mt-6 flex gap-3 justify-center">
          <Button asChild><Link to="/">Ana Sayfaya Dön</Link></Button>
          <Button variant="outline" asChild><Link to="/okullar">Okulları Keşfet</Link></Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-16" data-testid="school-signup-page">
      <div className="text-center mb-10">
        <div className="overline text-accent">Okul Kayıt</div>
        <h1 className="font-display text-4xl font-bold flex items-center justify-center gap-2">
          <School className="h-8 w-8 text-primary" /> Okulunuzu SanoStudent'a Ekleyin
        </h1>
        <p className="text-muted-foreground mt-3 max-w-lg mx-auto">
          Aşağıdaki formu doldurun; yönetici onayı sonrası okulunuz bursluluk platformumuzda yayına alınacaktır.
        </p>
      </div>

      <div className="rounded-xl border border-border bg-card p-6 space-y-4">
        <div>
          <Label className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" /> Okul Adı *</Label>
          <Input value={form.school_name} onChange={(e) => set("school_name", e.target.value)} data-testid="signup-name" />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>İl *</Label>
            <Select value={form.province} onValueChange={(v) => set("province", v)}>
              <SelectTrigger data-testid="signup-province"><SelectValue placeholder="İl Seçiniz" /></SelectTrigger>
              <SelectContent>
                {provinces.map(p => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>İlçe *</Label>
            <Select value={form.district} onValueChange={(v) => set("district", v)} disabled={!form.province}>
              <SelectTrigger data-testid="signup-district"><SelectValue placeholder="İlçe Seçiniz" /></SelectTrigger>
              <SelectContent>
                {form.province && turkeyData[form.province] ? turkeyData[form.province].map(d => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                )) : null}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>Yetkili Ad Soyad *</Label>
          <Input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="signup-contact-name" />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> E-posta *</Label>
            <Input type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} placeholder="mudur@okulunuz.k12.tr" data-testid="signup-email" />
            <div className="text-[11px] text-muted-foreground mt-1">
              Kurumsal e-posta (@meb.k12.tr / @edu.tr) tercih edilir — hızlı onay için.
            </div>
          </div>
          <div>
            <Label className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> Telefon *</Label>
            <Input value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} placeholder="0XXX XXX XX XX" data-testid="signup-phone" />
          </div>
        </div>
        <div>
          <Label className="flex items-center gap-1"><Globe className="h-3.5 w-3.5" /> Web Sitesi (opsiyonel)</Label>
          <Input value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://okulunuz.k12.tr" data-testid="signup-website" />
        </div>
        <div>
          <Label>Kısa Tanıtım</Label>
          <Textarea value={form.short_intro} onChange={(e) => set("short_intro", e.target.value)} rows={3} placeholder="1-2 cümleyle okulunuzu tanıtın." data-testid="signup-intro" />
        </div>
        <div>
          <Label className="flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> MEB İzin Belgesi Bağlantısı (opsiyonel)</Label>
          <Input value={form.document_url} onChange={(e) => set("document_url", e.target.value)} placeholder="https://..." data-testid="signup-doc" />
          <div className="text-[11px] text-muted-foreground mt-1">
            Google Drive, Dropbox veya benzeri paylaşım bağlantısı. Onay sürecini hızlandırır.
          </div>
        </div>

        <Button
          onClick={submit}
          disabled={busy || !form.school_name || !form.province || !form.district || !form.contact_name || !form.contact_email || !form.contact_phone}
          className="w-full" size="lg"
          data-testid="signup-submit"
        >
          {busy ? "Gönderiliyor…" : "Başvuruyu Gönder"}
        </Button>
        <p className="text-[11px] text-muted-foreground text-center">
          Başvurunuzun incelenmesi 1-3 iş günü sürer. Onay sonrası size otomatik giriş bilgileri gönderilir.
        </p>
      </div>
    </div>
  );
}
